import {FunctionComponent, ReactNode, useCallback, useMemo} from "react";
import * as MuiLab from '@mui/lab';
import {Typography} from "@mui/material";
import classNames from "classnames";

export interface TimelineItemsEntry {
    key: string | number;
    disabled?: boolean;
    contentPrimaryText?: ReactNode;
    contentSecondaryText?: ReactNode;
    oppositeContentPrimaryText?: ReactNode;
    oppositeContentSecondaryText?: ReactNode;
}

export interface TimelineProps extends Omit<MuiLab.TimelineProps, 'children' | 'ref' | 'color'> {
    title?: string;
    items: Array<TimelineItemsEntry>;
    selectedItemKeys?: Array<TimelineItemsEntry['key']>;
    onItemClick?: (key: TimelineItemsEntry['key']) => void;
    color: 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';
    disabled?: boolean,
    loading?: boolean,
    loadingItemsCount?: number,
    loadingItem?: Omit<TimelineItemsEntry, 'key' | 'disabled'>,
}

const Timeline: FunctionComponent<TimelineProps> = (props) => {
    const {
        title, items, loading, selectedItemKeys: SIK,
        loadingItem, loadingItemsCount, onItemClick,
        ...timelineProps
    } = props;
    const selectedItemKeys = useMemo(() => SIK ?? [], [SIK]);

    return (
        <>
            <MuiLab.Timeline {...timelineProps}>
                <TimelineItem start contentPrimaryText={title ?? "Timeline"}/>
                {
                    items.map(item => (
                        <TimelineItem
                            {...item}
                            key={item.key}
                            selected={selectedItemKeys.includes(item.key)}
                            disabled={item.disabled}
                            {...(!props.disabled && {
                                onClick: () => onItemClick && onItemClick(item.key)
                            })}
                        />
                    ))
                }
                {
                    loading &&
                    Array.from({length: loadingItemsCount ?? 6}).map((_, key) => (
                        <TimelineItem
                            disabled
                            key={`LOADING_KEY_${key}`}
                            contentPrimaryText={loadingItem?.contentPrimaryText}
                            contentSecondaryText={loadingItem?.contentSecondaryText}
                            oppositeContentPrimaryText={loadingItem?.oppositeContentPrimaryText}
                            oppositeContentSecondaryText={loadingItem?.oppositeContentSecondaryText}
                        />
                    ))
                }
                <TimelineItem end/>
            </MuiLab.Timeline>
        </>
    );
}

interface TimelineItemProps extends Omit<MuiLab.TimelineItemProps, 'children' | 'ref'>, Omit<TimelineItemsEntry, 'key'> {
    end?: boolean;
    start?: boolean;
    selected?: boolean,
    disabled?: boolean,
    onClick?: () => void;
}

const TimelineItem: FunctionComponent<TimelineItemProps> = (props) => {

    const onClick = useCallback(() => {
        if (!props.disabled)
            props.onClick?.();
    }, [props])

    return (
        <MuiLab.TimelineItem className={classNames(props.className, {
            'selected': props.selected,
            'clickable': !props.disabled && !!props.onClick,
            'disabled': props.disabled,
        })}>
            {
                (!!props.oppositeContentPrimaryText || !!props.oppositeContentSecondaryText) && (
                    <MuiLab.TimelineOppositeContent onClick={onClick}>
                        {
                            props.oppositeContentPrimaryText &&
                            <Typography
                                {...props.disabled && {color: 'text.disabled'}}
                                variant={'body2'}
                                fontWeight={500}
                                sx={{wordBreak: 'break-all'}}
                            >
                                {props.oppositeContentPrimaryText}
                            </Typography>
                        }
                        {
                            props.oppositeContentSecondaryText &&
                            <Typography
                                {...props.disabled && {color: 'text.disabled'}}
                                variant={'caption'}
                                sx={{wordBreak: 'break-all'}}
                            >
                                {props.oppositeContentSecondaryText}
                            </Typography>
                        }
                    </MuiLab.TimelineOppositeContent>
                )
            }
            <MuiLab.TimelineSeparator>
                <MuiLab.TimelineDot
                    variant={props.end || props.start ? 'app-specific-end' : 'app-specific'}
                    onClick={onClick}
                />
                {
                    !props.end &&
                    <MuiLab.TimelineConnector/>
                }
            </MuiLab.TimelineSeparator>
            {
                (!!props.contentPrimaryText || !!props.contentSecondaryText || props.start || props.end) &&
                <MuiLab.TimelineContent onClick={onClick}>
                    {
                        props.contentPrimaryText &&
                        <Typography
                            {...props.disabled && {color: 'text.disabled'}}
                            variant={props.start ? 'body1' : 'body2'}
                            fontWeight={600}
                            sx={{wordBreak: 'break-all'}}
                        >
                            {props.contentPrimaryText}
                        </Typography>
                    }
                    {
                        props.contentSecondaryText &&
                        <Typography
                            {...props.disabled && {color: 'text.disabled'}}
                            variant={'caption'}
                            sx={{wordBreak: 'break-all'}}
                        >
                            {props.contentSecondaryText}
                        </Typography>
                    }
                </MuiLab.TimelineContent>
            }
        </MuiLab.TimelineItem>
    );
}


export default Timeline;
